// iot-admin/client/src/components/DeviceSimTaxiDriver.js
// SIM card, taxi/vehicle, and driver (with history) sections for the
// inventory device detail page. Reads from the device object returned by
// deviceService.getById and calls onRefresh() after any mutation.
//
// ── THE DRIVER SECTION IS DELIBERATELY NOT A FORM ───────────────────────────
// It used to be: an always-open, pre-filled name+mobile with a primary Save.
// Since V249 that was actively dangerous, because Save writes the PERSON.
//
// The natural gesture for "Ramesh drives this cab now" was to type over the
// name and Save — which RENAMED the existing driver everywhere: every other cab
// they drive, their whole history, and their KYC, now filed under the wrong
// human. Before V249 it corrupted one assignment row; after, it corrupts the
// entity. Nothing warned, and it looked like it worked.
//
// So the current driver is READ-ONLY by default, and the two intents are
// separate actions that cannot be confused:
//
//   Change driver   -> search for an existing PERSON and link them (or, only if
//                      they are genuinely new, create one). Never touches the
//                      outgoing driver's record.
//   Edit details    -> explicitly editing THE PERSON, behind a toggle that says
//                      how many cabs the change will affect.
//
// Documents also no longer create anybody: uploading a licence used to call
// assignDriver as a side effect, so an upload could silently invent a person
// and put them in a cab. Uploads now require an assigned driver.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { deviceService, driverService, vehicleService } from '../services/api';
import { ImageField } from './DeviceDocImage';
import './DeviceSimTaxiDriver.css';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// Driver document type → the payload field used by updateDriver/assignDriver.
const DRIVER_DOC_FIELD = {
  driver_image: 'imageKey',
  driver_aadhaar: 'aadhaarKey',
  driver_pan: 'panKey',
  driver_license: 'licenseKey',
};

const DeviceSimTaxiDriver = ({ device, onRefresh, onMessage, onError }) => {
  const deviceId = device.id;
  const sim = device.sim || {};
  const taxi = device.taxi || {};
  const vehicle = device.vehicle || null;
  const drivers = device.drivers || [];
  const activeDriver = device.activeDriver || null;
  const history = drivers.filter((d) => !d.isActive);

  // One line about the car's paperwork, so a lapsed certificate is visible from
  // the device without duplicating the vehicle screen's per-document breakdown.
  const docsBadge = (() => {
    if (!vehicle) return { cls: '', text: '' };
    const expiry = vehicle.soonestExpiry ? new Date(vehicle.soonestExpiry) : null;
    const days = expiry ? Math.round((expiry - new Date().setHours(0, 0, 0, 0)) / 86400000) : null;
    if (days !== null && days < 0) return { cls: 'status-error', text: `Paperwork expired ${Math.abs(days)} days ago` };
    if (days !== null && days <= 30) return { cls: 'status-pending', text: `Paperwork expires in ${days} days` };
    if (vehicle.documentsOnFile < vehicle.documentsTotal) {
      return { cls: 'status-disabled', text: `${vehicle.documentsOnFile} of ${vehicle.documentsTotal} documents on file` };
    }
    return { cls: 'status-active', text: 'Documents complete' };
  })();

  const [uploading, setUploading] = useState({});   // { docType: bool }
  const [editingSim, setEditingSim] = useState(false);
  const [simForm, setSimForm] = useState({ simNumber: '', simImsiNumber: '', simMobileNumber: '', lockPinCode: '' });
  const [savingSim, setSavingSim] = useState(false);

  // driverMode: 'view' (read-only, the default) | 'edit' (edit the PERSON)
  //            | 'change' (search/assign a different person)
  const [driverMode, setDriverMode] = useState('view');
  const [driverForm, setDriverForm] = useState({ name: '', mobile: '' });
  const [savingDriver, setSavingDriver] = useState(false);

  // Change-driver search state.
  const [search, setSearch] = useState('');
  const [results, setResults] = useState(null);   // null = not searched yet
  const [searching, setSearching] = useState(false);

  // vehicleMode: 'view' (the default) | 'change' (search and fit a car).
  // There is no 'edit' — a car is edited on the car, never from a device.
  const [vehicleMode, setVehicleMode] = useState('view');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleResults, setVehicleResults] = useState(null);
  const [searchingVehicles, setSearchingVehicles] = useState(false);
  const [fitting, setFitting] = useState(false);

  // Any change to who is assigned drops back to the safe read-only view, so an
  // edit form is never left open over a driver it was not opened for.
  useEffect(() => {
    setDriverMode('view');
    setDriverForm({ name: '', mobile: '' });
    setSearch(''); setResults(null);
  }, [activeDriver?.id]);

  const msg = (m) => onMessage && onMessage(m);
  const err = (e) => onError && onError(e);

  // ── device-level image upload (sim card / envelope / number plate) ──
  const uploadDeviceDoc = async (docType, file) => {
    setUploading((u) => ({ ...u, [docType]: true }));
    try {
      const key = await deviceService.uploadDocument(deviceId, docType, file);
      await deviceService.saveDocument(deviceId, docType, key);
      msg('Image uploaded');
      onRefresh && (await onRefresh());
    } catch (e) {
      err(e.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [docType]: false }));
    }
  };

  // ── SIM + lock PIN text fields ──
  const startEditSim = () => {
    setSimForm({
      simNumber: sim.number || '',
      simImsiNumber: sim.imsi || '',
      simMobileNumber: sim.mobileNumber || '',
      lockPinCode: taxi.lockPinCode || '',
    });
    setEditingSim(true);
  };

  const saveSim = async () => {
    setSavingSim(true);
    try {
      await deviceService.update(deviceId, simForm);
      setEditingSim(false);
      msg('Details saved');
      onRefresh && (await onRefresh());
    } catch (e) {
      err(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSavingSim(false);
    }
  };

  // ── EDIT THE PERSON ─────────────────────────────────────────────────────
  // Explicit, and only reachable from the "Edit details" toggle. This writes
  // the drivers row, so it changes the human on EVERY cab they drive and
  // throughout their history — which is correct for a typo or a new number, and
  // catastrophic if what was meant was "a different person drives this cab".
  // The UI states the blast radius before the click; "Change driver" is the
  // other path and is offered right next to it.
  const savePersonEdits = async () => {
    if (driverForm.name.trim().length < 2) { err('Driver name is required (min 2 chars)'); return; }
    setSavingDriver(true);
    try {
      await deviceService.updateDriver(deviceId, activeDriver.id, {
        name: driverForm.name.trim(),
        mobile: driverForm.mobile.trim() || null,
      });
      msg('Driver details updated');
      setDriverMode('view');
      onRefresh && (await onRefresh());
    } catch (e) {
      // 409 = that mobile already belongs to a different person. The server
      // refuses rather than merging two humans; surface it as-is.
      err(e.response?.data?.error || e.message || 'Failed to update driver');
    } finally {
      setSavingDriver(false);
    }
  };

  // ── CHANGE WHO IS DRIVING ───────────────────────────────────────────────
  const runSearch = async (q) => {
    setSearch(q);
    if (q.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    try {
      const res = await driverService.search(q.trim());
      setResults(res.data || []);
    } catch (e) {
      err(e.response?.data?.error || 'Driver search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  // ── Vehicle ──────────────────────────────────────────────────────────────
  const runVehicleSearch = async (q) => {
    setVehicleSearch(q);
    if (q.trim().length < 2) { setVehicleResults(null); return; }
    setSearchingVehicles(true);
    try {
      const res = await vehicleService.search(q.trim());
      setVehicleResults(res.data || []);
    } catch (e) {
      err(e.response?.data?.error || 'Vehicle search failed');
      setVehicleResults([]);
    } finally {
      setSearchingVehicles(false);
    }
  };

  // Link an EXISTING car. Sends vehicleId only — nothing about the car is
  // written from here, which is what stops a device page from silently editing
  // a vehicle's particulars.
  const fitVehicle = async (v) => {
    if (v.activeDevices > 0 && !window.confirm(
      `${v.registration} already carries ${v.fittedDevices}.\n\n` +
      'Fitting this device as well is allowed — a car may have a front and a rear ' +
      'screen — but confirm it is intended rather than a missed removal.'
    )) return;
    setFitting(true);
    try {
      await deviceService.fitVehicle(deviceId, v.id);
      msg(`Fitted in ${v.registration}`);
      setVehicleMode('view');
      setVehicleSearch('');
      setVehicleResults(null);
      await onRefresh?.();
    } catch (e) {
      err(e.response?.data?.error || 'Failed to fit vehicle');
    } finally {
      setFitting(false);
    }
  };

  const unfitVehicle = async () => {
    if (!window.confirm(
      `Remove this device from ${vehicle.registration}?\n\n` +
      'The fitting is kept in history — it is the record of where this screen was serving.'
    )) return;
    setFitting(true);
    try {
      await deviceService.unfitVehicle(deviceId);
      msg('Removed from vehicle');
      await onRefresh?.();
    } catch (e) {
      err(e.response?.data?.error || 'Failed to remove from vehicle');
    } finally {
      setFitting(false);
    }
  };

  // Link an EXISTING person. Sends driverId only, so nothing about them is
  // rewritten — the whole point of the entity.
  const assignExisting = async (person) => {
    if (person.activeAssignments > 0 && !window.confirm(
      `${person.name} is currently active on ${person.activeCab || 'another cab'}.\n\n` +
      'Assigning them here as well is allowed — one driver can hold several cabs — ' +
      'but if this is a mistake, cancel and pick someone else.\n\nAssign anyway?'
    )) return;
    setSavingDriver(true);
    try {
      await deviceService.assignDriver(deviceId, { driverId: person.id });
      msg(`${person.name} assigned`);
      setDriverMode('view'); setSearch(''); setResults(null);
      onRefresh && (await onRefresh());
    } catch (e) {
      err(e.response?.data?.error || e.message || 'Failed to assign driver');
    } finally {
      setSavingDriver(false);
    }
  };

  // Create a person who is genuinely new to the fleet. Only offered once a
  // search has actually run, so "create" is never the first thing reached for.
  const assignNewPerson = async () => {
    if (driverForm.name.trim().length < 2) { err('Driver name is required (min 2 chars)'); return; }
    setSavingDriver(true);
    try {
      await deviceService.assignDriver(deviceId, {
        name: driverForm.name.trim(),
        mobile: driverForm.mobile.trim() || null,
      });
      msg('Driver assigned');
      setDriverMode('view'); setSearch(''); setResults(null);
      setDriverForm({ name: '', mobile: '' });
      onRefresh && (await onRefresh());
    } catch (e) {
      err(e.response?.data?.error || e.message || 'Failed to assign driver');
    } finally {
      setSavingDriver(false);
    }
  };

  const startChange = () => { setDriverMode('change'); setDriverForm({ name: '', mobile: '' }); setSearch(''); setResults(null); };
  const startEdit = () => { setDriverMode('edit'); setDriverForm({ name: activeDriver?.name || '', mobile: activeDriver?.mobile || '' }); };
  const cancelDriverAction = () => { setDriverMode('view'); setSearch(''); setResults(null); };

  const unassignDriver = async (driverId) => {
    if (!window.confirm('Unassign the current driver? History is kept.')) return;
    try {
      await deviceService.unassignDriver(deviceId, driverId);
      msg('Driver unassigned');
      onRefresh && (await onRefresh());
    } catch (e) {
      err(e.response?.data?.error || e.message || 'Failed to unassign');
    }
  };

  // Upload a driver document (photo / Aadhaar / PAN / licence) onto the
  // ASSIGNED person.
  //
  // This used to call ensureDriverId(), which created a driver from whatever
  // was typed in the form when none was assigned — so uploading an Aadhaar
  // could silently invent a person and put them in a cab, before anyone pressed
  // save. Documents are now strictly an operation on an existing assignment:
  // no driver, no upload. Assign first.
  const uploadDriverDoc = async (docType, file) => {
    if (!activeDriver) { err('Assign a driver before uploading their documents'); return; }
    setUploading((u) => ({ ...u, [docType]: true }));
    try {
      const driverId = activeDriver.id;
      const key = await deviceService.uploadDocument(deviceId, docType, file);
      await deviceService.updateDriver(deviceId, driverId, { [DRIVER_DOC_FIELD[docType]]: key });
      msg('Image uploaded');
      onRefresh && (await onRefresh());
    } catch (e) {
      err(e.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [docType]: false }));
    }
  };

  return (
    <>
      {/* ── SIM Card ── */}
      <div className="device-detail" id="dd-sim">
        <h2><span>🚕 SIM, Vehicle &amp; Driver</span></h2>

        {/* SIM + lock PIN. Restored: a V250-era refactor dropped this form's
            JSX while keeping its state, handlers, server contract, and the
            section title that still promised it — the section literally said
            "SIM" and showed none. */}
        <h3 className="std-subhead">SIM card &amp; lock PIN</h3>
        {!editingSim ? (
          <div className="std-driver-card">
            <div className="detail-grid" style={{ marginBottom: 0 }}>
              <div className="detail-item"><label>SIM number</label><span className="std-muted">{sim.number || '—'}</span></div>
              <div className="detail-item"><label>IMSI</label><span className="std-muted">{sim.imsi || '—'}</span></div>
              <div className="detail-item"><label>Mobile number</label><span className="std-muted">{sim.mobileNumber || '—'}</span></div>
              <div className="detail-item"><label>Lock PIN</label><span className="std-muted">{taxi.lockPinCode ? '••••' : '—'}</span></div>
            </div>
            <div className="std-form-actions">
              <button className="btn btn-secondary" onClick={startEditSim}>Edit SIM details</button>
            </div>
          </div>
        ) : (
          <div className="std-driver-card">
            <div className="detail-grid" style={{ marginBottom: 0 }}>
              <div className="detail-item">
                <label>SIM number</label>
                <input className="std-input" value={simForm.simNumber}
                  onChange={(e) => setSimForm(f => ({ ...f, simNumber: e.target.value }))} placeholder="SIM / ICCID" />
              </div>
              <div className="detail-item">
                <label>IMSI</label>
                <input className="std-input" value={simForm.simImsiNumber}
                  onChange={(e) => setSimForm(f => ({ ...f, simImsiNumber: e.target.value }))} placeholder="IMSI" />
              </div>
              <div className="detail-item">
                <label>Mobile number</label>
                <input className="std-input" value={simForm.simMobileNumber}
                  onChange={(e) => setSimForm(f => ({ ...f, simMobileNumber: e.target.value }))} placeholder="+91…" />
              </div>
              <div className="detail-item">
                <label>Lock PIN</label>
                <input className="std-input" value={simForm.lockPinCode}
                  onChange={(e) => setSimForm(f => ({ ...f, lockPinCode: e.target.value }))} placeholder="Device lock PIN" />
              </div>
            </div>
            <div className="std-form-actions">
              <button className="btn btn-primary" disabled={savingSim} onClick={saveSim}>
                {savingSim ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-secondary" disabled={savingSim} onClick={() => setEditingSim(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── Vehicle ── */}
        {/* Link only. Since V250 the car is an entity with its own papers, so
            this section says WHICH car and nothing about it — editing the car
            happens on the car, where the blast radius can be stated. The
            number-plate photo moved there too: read off the device it would
            follow the tablet into whatever car it was moved to next. */}
        <h3 className="std-subhead" id="dd-vehicle">
          Vehicle
          {vehicle && <span className="std-muted"> · fitted {fmtDate(vehicle.fittedAt)}</span>}
        </h3>

        {vehicle && vehicleMode === 'view' && (
          <div className="std-driver-card">
            <div className="std-images" style={{ marginTop: 0 }}>
              <ImageField label="Number plate" url={vehicle.numberPlateImageUrl} size="sm" />
            </div>
            <div className="std-driver-meta">
              <Link to={`/vehicles/${vehicle.id}`}><strong>🚗 {vehicle.registration}</strong></Link>
              <span className="std-muted">
                {[vehicle.make, vehicle.model, vehicle.modelYear, vehicle.colour, vehicle.fuelType?.toUpperCase()]
                  .filter(Boolean).join(' · ') || 'No particulars on file'}
              </span>
              {/* Papers are the car's business, but a screen sitting in a cab
                  with lapsed insurance is the operator's problem too, so the
                  device page says just enough to notice. */}
              <span className={`status-badge ${docsBadge.cls}`} style={{ fontSize: '0.7rem', width: 'fit-content' }}>
                {docsBadge.text}
              </span>
            </div>
            <div className="std-driver-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setVehicleMode('change')}>
                🔄 Change vehicle
              </button>
              <button className="btn btn-secondary btn-sm" onClick={unfitVehicle} disabled={fitting}>
                {fitting ? 'Working…' : 'Remove'}
              </button>
            </div>
          </div>
        )}

        {!vehicle && vehicleMode === 'view' && (
          <p className="std-hint">
            Not fitted in a vehicle.{' '}
            <button className="btn btn-primary btn-sm" onClick={() => setVehicleMode('change')}>
              🚗 Fit into a vehicle
            </button>
          </p>
        )}

        {vehicleMode === 'change' && (
          <>
            {vehicle && (
              <p className="std-hint">
                Fitting a different car retires <strong>{vehicle.registration}</strong> from this
                device (kept in history). The car&rsquo;s own record is not changed.
              </p>
            )}

            <div className="detail-grid">
              <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                <label>Find an existing vehicle</label>
                <input className="std-input" value={vehicleSearch} autoFocus
                  placeholder="Search by registration, make or model…"
                  onChange={(e) => runVehicleSearch(e.target.value)} />
                <small className="std-muted">
                  Spacing is ignored — &ldquo;mh 03 eg&rdquo; finds MH03EG6307.
                </small>
              </div>
            </div>

            {searchingVehicles && <p className="std-hint">Searching…</p>}

            {vehicleResults && vehicleResults.length > 0 && (
              <table className="std-history">
                <thead>
                  <tr><th>Vehicle</th><th>Particulars</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {vehicleResults.map((v) => (
                    <tr key={v.id}>
                      <td>{v.registration}</td>
                      <td className="std-muted">{[v.make, v.model].filter(Boolean).join(' ') || '—'}</td>
                      <td>
                        {v.activeDevices > 0
                          ? <span className="std-muted">carries {v.fittedDevices}</span>
                          : <span className="std-muted">no screen fitted</span>}
                      </td>
                      <td>
                        <button className="btn btn-primary btn-sm" disabled={fitting}
                          onClick={() => fitVehicle(v)}>Fit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* No "create vehicle" here on purpose. Typing a plate into a device
                page is precisely how registration numbers ended up in node_name;
                cars are created where their papers can be attached. */}
            {vehicleResults && vehicleResults.length === 0 && !searchingVehicles && (
              <p className="std-hint">
                No vehicle matches. <Link to="/vehicles">Create it on the Vehicles screen</Link>,
                then come back and fit it here.
              </p>
            )}

            <div className="std-form-actions">
              <button className="btn btn-secondary btn-sm" disabled={fitting}
                onClick={() => { setVehicleMode('view'); setVehicleSearch(''); setVehicleResults(null); }}>
                Cancel
              </button>
            </div>
          </>
        )}

        <h3 className="std-subhead" id="dd-driver">
          Driver
          {activeDriver && driverMode === 'view' && (
            <span className="std-muted"> · since {fmtDate(activeDriver.assignedAt)}</span>
          )}
        </h3>

        {/* ── VIEW: read-only. Nothing here can accidentally rewrite a person. ── */}
        {driverMode === 'view' && (
          activeDriver ? (
            <>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Name</label>
                  <span>{activeDriver.name}</span>
                </div>
                <div className="detail-item">
                  <label>Mobile</label>
                  <span>{activeDriver.mobile || '—'}</span>
                </div>
                <div className="detail-item std-form-actions">
                  {/* Change is the COMMON action, so it is primary. Editing the
                      person is rare and deliberately quieter. */}
                  <button className="btn btn-primary btn-sm" onClick={startChange}>🔄 Change driver</button>
                  <button className="btn btn-secondary btn-sm" onClick={startEdit}>✏️ Edit details</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => unassignDriver(activeDriver.id)}>Unassign</button>
                </div>
              </div>
              {activeDriver.otherAssignments > 0 && (
                <p className="std-hint">
                  🔗 Also assigned to {activeDriver.otherAssignments} other
                  cab{activeDriver.otherAssignments === 1 ? '' : 's'}.
                </p>
              )}
            </>
          ) : (
            <>
              <p className="std-hint">No driver assigned to this cab.</p>
              <div className="std-form-actions">
                <button className="btn btn-primary btn-sm" onClick={startChange}>➕ Assign driver</button>
              </div>
            </>
          )
        )}

        {/* ── EDIT: the person. Blast radius stated before the click. ── */}
        {driverMode === 'edit' && activeDriver && (
          <>
            <p className="std-hint std-warn">
              ⚠️ This edits <strong>the person</strong>, not just this cab.
              {activeDriver.otherAssignments > 0
                ? ` ${activeDriver.name} is on ${activeDriver.otherAssignments + 1} cabs — the change applies to all of them and to their history.`
                : ' The change applies to their history and any future cab.'}
              {' '}If a <em>different</em> person now drives this cab, cancel and use
              &ldquo;Change driver&rdquo; instead.
            </p>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Name</label>
                <input className="std-input" value={driverForm.name} placeholder="Full name"
                  onChange={(e) => setDriverForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="detail-item">
                <label>Mobile</label>
                <input className="std-input" value={driverForm.mobile} placeholder="+91…"
                  onChange={(e) => setDriverForm((f) => ({ ...f, mobile: e.target.value }))} />
              </div>
              <div className="detail-item std-form-actions">
                <button className="btn btn-primary btn-sm" onClick={savePersonEdits} disabled={savingDriver}>
                  {savingDriver ? 'Saving…' : '💾 Save person'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={cancelDriverAction} disabled={savingDriver}>Cancel</button>
              </div>
            </div>
          </>
        )}

        {/* ── CHANGE: search first, create only as a fallback. ── */}
        {driverMode === 'change' && (
          <>
            {activeDriver && (
              <p className="std-hint">
                Assigning someone else retires <strong>{activeDriver.name}</strong> from this cab
                (kept in history). Their own record is not changed.
              </p>
            )}

            <div className="detail-grid">
              <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                <label>Find an existing driver</label>
                <input className="std-input" value={search} autoFocus
                  placeholder="Search by name or mobile…"
                  onChange={(e) => runSearch(e.target.value)} />
              </div>
            </div>

            {searching && <p className="std-hint">Searching…</p>}

            {results && results.length > 0 && (
              <table className="std-history">
                <thead>
                  <tr><th>Driver</th><th>Mobile</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name}{r.hasKyc && <span className="std-muted"> · KYC on file</span>}</td>
                      <td>{r.mobile || '—'}</td>
                      <td>
                        {r.activeAssignments > 0
                          ? <span className="std-muted">on {r.activeCab || 'another cab'}</span>
                          : <span className="std-muted">{r.assignments > 0 ? 'free' : 'never assigned'}</span>}
                      </td>
                      <td>
                        <button className="btn btn-primary btn-sm" disabled={savingDriver}
                          onClick={() => assignExisting(r)}>Assign</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Create is reachable only AFTER a search has run and found
                nothing, so "add a new person" is never the first thing to hand
                — which is how duplicate humans get created. */}
            {results && results.length === 0 && (
              <>
                <p className="std-hint">
                  No existing driver matches &ldquo;{search}&rdquo;. If they are new to the fleet,
                  add them below — otherwise refine the search, because creating a second record
                  for the same person splits their history and KYC.
                </p>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name</label>
                    <input className="std-input" value={driverForm.name} placeholder="Full name"
                      onChange={(e) => setDriverForm((f) => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="detail-item">
                    <label>Mobile</label>
                    <input className="std-input" value={driverForm.mobile} placeholder="+91…"
                      onChange={(e) => setDriverForm((f) => ({ ...f, mobile: e.target.value }))} />
                  </div>
                  <div className="detail-item std-form-actions">
                    <button className="btn btn-primary btn-sm" onClick={assignNewPerson} disabled={savingDriver}>
                      {savingDriver ? 'Assigning…' : '➕ Create & assign'}
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="std-form-actions">
              <button className="btn btn-secondary btn-sm" onClick={cancelDriverAction} disabled={savingDriver}>Cancel</button>
            </div>
          </>
        )}

        {/* Driver documents (KYC) — belong to the PERSON, and only exist once
            someone is assigned. Hidden entirely when the cab has no driver,
            because an upload with nobody assigned used to invent one. */}
        {activeDriver && (
          <>
            <h3 className="std-subhead">
              Documents
              <span className="std-muted"> · {activeDriver.name}</span>
            </h3>
            {activeDriver.otherAssignments > 0 && (
              <p className="std-hint">
                These belong to the person, so they are the same documents shown on their
                other {activeDriver.otherAssignments === 1 ? 'cab' : 'cabs'}.
              </p>
            )}
            <div className="std-images std-driver-docs">
              <ImageField label="Driver Photo" url={activeDriver.imageUrl} uploading={uploading.driver_image}
                onUpload={(f) => uploadDriverDoc('driver_image', f)} size="sm" />
              <ImageField label="Aadhaar" url={activeDriver.aadhaarUrl} uploading={uploading.driver_aadhaar}
                onUpload={(f) => uploadDriverDoc('driver_aadhaar', f)} size="sm" />
              <ImageField label="PAN" url={activeDriver.panUrl} uploading={uploading.driver_pan}
                onUpload={(f) => uploadDriverDoc('driver_pan', f)} size="sm" />
              <ImageField label="Driving Licence" url={activeDriver.licenseUrl} uploading={uploading.driver_license}
                onUpload={(f) => uploadDriverDoc('driver_license', f)} size="sm" />
            </div>
          </>
        )}

        {/* History */}
        {history.length > 0 && (
          <>
            <h3 className="std-subhead">Driver History</h3>
            <table className="std-history">
              <thead>
                <tr><th>Driver</th><th>Mobile</th><th>Assigned</th><th>Unassigned</th></tr>
              </thead>
              <tbody>
                {history.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <span className="std-history-driver">
                        {d.imageUrl && <img src={d.imageUrl} alt={d.name} className="std-history-avatar" />}
                        {d.name}
                      </span>
                    </td>
                    <td>{d.mobile || '—'}</td>
                    <td>{fmtDate(d.assignedAt)}</td>
                    <td>{fmtDate(d.unassignedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </>
  );
};

export default DeviceSimTaxiDriver;
